import { Box, Button, Chip, MenuItem, TextField } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { AppError } from '../../api/contract';
import { useAuth } from '../../auth/AuthProvider';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { PageState } from '../../shared/data/PageState';
import { ServerDataGrid } from '../../shared/data/ServerDataGrid';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { PageHeader } from '../../shared/layout/PageHeader';
import { ListFilterPanel } from '../../shared/filter/ListFilterPanel';
import type { PageResult } from '../../shared/data/types';
import { PartnerFormDialog } from './PartnerFormDialog';
import { defaultPartnerListQuery, partnerRepositoryToken } from './repository';
import type { PartnerFilters, PartnerListQuery, PartnerSummary } from './types';

export function PartnerListPage({ dataSource }: { readonly dataSource: DataSource }) {
  const repository = useRepository(partnerRepositoryToken);
  const auth = useAuth();
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [query, setQuery] = useState<PartnerListQuery>(defaultPartnerListQuery);
  const [refreshKey, setRefreshKey] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const resource = useResource(
    (signal) => repository.list(query, signal),
    [repository, query, refreshKey],
    (page) => page.items.length === 0,
  );
  const canManage =
    auth.status === 'authenticated' && auth.principal.permissions.includes('PARTNER_MANAGE');
  const columns = useMemo<readonly GridColDef<PartnerSummary>[]>(
    () => [
      {
        field: 'code',
        headerName: t('partners.fields.code'),
        minWidth: 130,
        renderCell: ({ row }) => <Link to={`/partners/${row.id}`}>{row.code}</Link>,
      },
      { field: 'name', headerName: t('partners.fields.name'), flex: 1, minWidth: 180 },
      {
        field: 'status',
        headerName: t('partners.fields.status'),
        minWidth: 120,
        renderCell: ({ row }) => (
          <Chip
            color={
              row.status === 'ACTIVE'
                ? 'success'
                : row.status === 'SUSPENDED'
                  ? 'warning'
                  : 'default'
            }
            label={row.status}
            size="small"
          />
        ),
      },
      {
        field: 'defaultCurrencyCode',
        headerName: t('partners.fields.currency'),
        minWidth: 110,
        sortable: false,
      },
      {
        field: 'timezone',
        headerName: t('partners.fields.timezone'),
        minWidth: 160,
        sortable: false,
      },
      {
        field: 'requestRateLimitPerMinute',
        headerName: t('partners.fields.requestRateLimit'),
        minWidth: 150,
        sortable: false,
      },
      {
        field: 'updatedAt',
        headerName: t('partners.fields.updatedAt'),
        minWidth: 190,
        valueFormatter: (value: string) => formatTimestamp(value, 'Asia/Bangkok', language).display,
      },
    ],
    [language, t],
  );

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        actions={
          <>
            <DataSourceBadge source={dataSource} />
            {canManage ? (
              <Button onClick={() => setCreateOpen(true)} variant="contained">
                {t('partners.actions.create')}
              </Button>
            ) : null}
          </>
        }
        subtitle={t('partners.subtitle')}
        title={t('partners.title')}
      />
      <PartnerFiltersForm
        onApply={(filters) => setQuery((current) => ({ ...current, page: 1, filters }))}
        value={query.filters}
      />
      <PageState
        emptyMessage={t('partners.empty')}
        error={toPageError(resource.error, t('partners.errors.load'))}
        onRetry={() => setRefreshKey((key) => key + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <PartnerGrid
            columns={columns}
            onQueryChange={setQuery}
            page={resource.data}
            query={query}
          />
        )}
      </PageState>
      <PartnerFormDialog
        onClose={() => setCreateOpen(false)}
        onSaved={() => setRefreshKey((key) => key + 1)}
        open={createOpen}
        repository={repository}
      />
    </Box>
  );
}

function PartnerGrid({
  columns,
  onQueryChange,
  page,
  query,
}: {
  readonly columns: readonly GridColDef<PartnerSummary>[];
  readonly onQueryChange: (query: PartnerListQuery) => void;
  readonly page: PageResult<PartnerSummary>;
  readonly query: PartnerListQuery;
}) {
  const gridQuery = { ...query, page: query.page - 1 };
  const { t } = useTranslation();
  return (
    <Box
      aria-label={t('partners.gridLabel')}
      role="region"
      sx={{ maxWidth: '100%', overflowX: 'auto' }}
      tabIndex={0}
    >
      <ServerDataGrid
        columns={columns}
        disableRowSelectionOnClick
        onQueryChange={(next) => onQueryChange({ ...next, page: next.page + 1 })}
        page={page}
        query={gridQuery}
        sx={{ minWidth: 920 }}
      />
    </Box>
  );
}

function PartnerFiltersForm({
  onApply,
  value,
}: {
  readonly onApply: (filters: PartnerFilters) => void;
  readonly value: PartnerFilters;
}) {
  const { t } = useTranslation();
  const { handleSubmit, register, reset } = useForm<PartnerFilters>({ defaultValues: value });
  return (
    <ListFilterPanel
      applyLabel={t('partners.actions.applyFilters')}
      ariaLabel={t('filterPanel.label', { page: t('partners.title') })}
      onClear={() => {
        reset(defaultPartnerListQuery.filters);
        onApply(defaultPartnerListQuery.filters);
      }}
      onSubmit={handleSubmit((filters) =>
        onApply({
          code: filters.code.trim(),
          name: filters.name.trim(),
          status: filters.status,
          currency: filters.currency.trim().toUpperCase(),
        }),
      )}
      primaryFields={
        <>
          <TextField label={t('partners.filters.code')} size="small" {...register('code')} />
          <TextField label={t('partners.filters.name')} size="small" {...register('name')} />
          <TextField
            defaultValue=""
            label={t('partners.filters.status')}
            select
            size="small"
            {...register('status')}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            <MenuItem value="ACTIVE">ACTIVE</MenuItem>
            <MenuItem value="SUSPENDED">SUSPENDED</MenuItem>
            <MenuItem value="CLOSED">CLOSED</MenuItem>
          </TextField>
          <TextField
            label={t('partners.filters.currency')}
            size="small"
            {...register('currency')}
          />
        </>
      }
    />
  );
}

export function toPageError(
  error: unknown,
  fallback: string,
): Pick<AppError, 'message' | 'requestId'> | null {
  if (error === null) return null;
  if (typeof error === 'object' && error !== null && 'message' in error && 'requestId' in error) {
    const candidate = error as { readonly message: unknown; readonly requestId: unknown };
    return {
      message: typeof candidate.message === 'string' ? candidate.message : fallback,
      requestId: typeof candidate.requestId === 'string' ? candidate.requestId : null,
    };
  }
  return { message: fallback, requestId: null };
}
